import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/utils/api';
import {
  ArrowLeft, Search, Trash2, ExternalLink, Shield, Briefcase, MapPin, Building, AlertTriangle, X, Image, Download
} from 'lucide-react';
import { MOD_TABS, ModerationDialog, ModerationInfo, ModerationButtons, modStatusLabel, useModerationDeepLink } from '@/components/admin/JobModeration';
import { JobTipsButton } from '@/components/admin/JobTips';

const STATUS_TABS = [
  { value: 'active',         label: 'Ativas',                color: 'bg-emerald-100 text-emerald-800' },
  { value: 'pending_review', label: 'A aguardar aprovação',  color: 'bg-blue-100 text-blue-800' },
  { value: 'paused',         label: 'Em pausa (empresa ou moderação)', color: 'bg-orange-100 text-orange-800' },
  { value: 'suspicious',     label: 'Suspeitas',             color: 'bg-red-100 text-red-800' },
  { value: 'expired',        label: 'Expiradas',             color: 'bg-gray-200 text-gray-700' },
  { value: 'no_apps',        label: 'Sem candidaturas há 7 dias', color: 'bg-amber-100 text-amber-800' },
  { value: '',               i18nKey: 'all',      color: 'bg-gray-100 text-gray-800' },
  { value: 'draft',          i18nKey: 'drafts',   color: 'bg-amber-100 text-amber-800' },
  ...MOD_TABS.filter((m) => !['pending_review', 'suspicious', 'moderation'].includes(m.value)),
];

export default function AdminJobs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') ?? 'active');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // {job, reason, notify}
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [generating, setGenerating] = useState(null); // job_id being generated
  const [postResult, setPostResult] = useState(null); // {story_url, feed_url, story_filename, feed_filename}
  const [modAction, setModAction] = useState(null); // {job, action}
  const openMod = useCallback((job, action) => setModAction({ job, action }), []);
  useModerationDeepLink(items, openMod);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab) params.status = tab === 'no_apps' ? 'active' : tab;
      if (search.trim()) params.q = search.trim();
      const { data } = await adminAPI.listJobs(params);
      setItems(tab === 'no_apps' ? data.filter((j) => j.no_apps_alert) : data);
    } catch (e) {
      setMessage(e?.response?.data?.detail || t('adminJobs.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e) => { e.preventDefault(); load(); };

  const generatePost = async (job) => {
    setGenerating(job.id);
    setPostResult(null);
    setMessage('');
    try {
      const { data } = await api.post(`/admin/jobs/${job.id}/generate-post`, {}, { timeout: 120000 });
      setPostResult({ ...data, jobTitle: job.title_pt });
    } catch (e) {
      setMessage('Erro ao gerar imagens: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setGenerating(null);
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    setSubmitting(true);
    setMessage('');
    try {
      const { data } = await adminAPI.deleteJob(confirm.job.id, confirm.reason, confirm.notify);
      setMessage(t('adminJobs.jobRemovedMsg', { apps: data.applications_removed, favs: data.favourites_removed }));
      setConfirm(null);
      await load();
    } catch (e) {
      setMessage(e?.response?.data?.detail || t('adminJobs.errorRemove'));
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s) => {
    const map = {
      active:  'bg-emerald-100 text-emerald-700',
      draft:   'bg-amber-100 text-amber-700',
      expired: 'bg-gray-200 text-gray-600',
      pending_review: 'bg-blue-100 text-blue-800',
      rejected: 'bg-rose-100 text-rose-800',
      paused: 'bg-orange-100 text-orange-800',
    };
    return <Badge className={map[s.status] || 'bg-gray-100 text-gray-700'}>{modStatusLabel(s)}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Shield className="h-7 w-7 text-blue-600" />
          <div className="flex-1 min-w-0 sm:min-w-[200px]">
            <h1 className="text-3xl font-bold text-gray-900">{t('adminJobs.title')}</h1>
            <p className="text-gray-600 text-sm">{t('adminJobs.subtitle')}</p>
          </div>
        </div>

        {message && (
          <Alert className="mb-4 border-blue-200 bg-blue-50">
            <AlertDescription className="text-blue-800" data-testid="admin-jobs-message">{message}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="p-4 mb-5">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {STATUS_TABS.map(tb => (
              <button
                key={tb.value || 'all'}
                onClick={() => setTab(tb.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  tab === tb.value ? `${tb.color} ring-2 ring-offset-1 ring-blue-400` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`admin-jobs-tab-${tb.value || 'all'}`}
              >
                {tb.label || t(`adminJobs.tab.${tb.i18nKey}`)}
              </button>
            ))}
          </div>
          <form onSubmit={onSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('adminJobs.searchPlaceholder')}
                className="pl-9"
                data-testid="admin-jobs-search-input"
              />
            </div>
            <Button type="submit" variant="outline" data-testid="admin-jobs-search-btn">{t('adminJobs.search')}</Button>
          </form>
        </Card>

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('adminJobs.loading')}</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center text-gray-500">
            <Briefcase className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            {t('adminJobs.noResults')}
          </Card>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-500 mb-2">{t('adminJobs.resultsCount', { count: items.length })}</div>
            {items.map(job => (
              <Card key={job.id} className="p-4" data-testid={`admin-job-row-${job.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-gray-900 truncate">{job.title_pt || t('adminJobs.untitled')}</h3>
                      {statusBadge(job)}
                      {job.category && <Badge variant="outline" className="text-xs">{job.category}</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap mb-2">
                      {job.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
                      <span className="flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        {job.employer_company || job.employer_email || job.employer_id}
                      </span>
                      <span className="text-gray-400">
                        {t('adminJobs.createdOn', { date: job.created_at ? new Date(job.created_at).toLocaleDateString('pt-PT') : '—' })}
                      </span>
                      <span className="font-semibold text-slate-600" data-testid={`admin-job-views-${job.id}`}>{job.views_count || 0} visualizações</span>
                      <span className="font-semibold text-slate-600" data-testid={`admin-job-apps-${job.id}`}>{job.applications_count || 0} candidaturas</span>
                    </div>
                    {job.no_apps_alert && (
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                      <p className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800" data-testid={`admin-job-noapps-${job.id}`}>
                        <AlertTriangle className="h-3.5 w-3.5" />Vaga sem candidaturas há 7 dias{job.last_application_at ? ` (última a ${new Date(job.last_application_at).toLocaleDateString('pt-PT')})` : ''} — contacte {job.employer_company || 'a empresa'} para ajudar{job.employer_email ? <a href={`mailto:${job.employer_email}`} className="ml-1 underline">{job.employer_email}</a> : null}
                      </p>
                      <JobTipsButton job={job} onSent={load} />
                      </div>
                    )}
                    {job.description_pt && (
                      <p className="text-sm text-gray-600 line-clamp-2">{job.description_pt}</p>
                    )}
                    <ModerationInfo job={job} />
                  </div>
                  <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end max-w-full sm:max-w-md">
                    <ModerationButtons job={job} onAction={openMod} />
                    {job.status === 'active' && <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generatePost(job)}
                      disabled={generating === job.id}
                      className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      data-testid={`admin-job-genpost-${job.id}`}
                    >
                      <Image className="h-3.5 w-3.5 mr-1" />
                      {generating === job.id ? 'A gerar…' : 'Post Story'}
                    </Button>}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/job/${job.id}`, '_blank')}
                      data-testid={`admin-job-view-${job.id}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />{t('adminJobs.view')}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setConfirm({ job, reason: '', notify: true })}
                      data-testid={`admin-job-delete-${job.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />{t('adminJobs.remove')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {confirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-delete-modal">
          <Card className="max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <h2 className="text-lg font-bold">{t('adminJobs.removeJobTitle')}</h2>
              </div>
              <button onClick={() => setConfirm(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              {t('adminJobs.confirmRemove')}
            </p>
            <p className="text-sm font-semibold text-gray-900 mb-4">"{confirm.job.title_pt || t('adminJobs.untitled')}"</p>
            <p className="text-xs text-gray-500 mb-4">
              {t('adminJobs.removeWarning')}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminJobs.reasonLabel')}</label>
            <Textarea
              value={confirm.reason}
              onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })}
              placeholder={t('adminJobs.reasonPlaceholder')}
              rows={3}
              className="mb-3"
              data-testid="admin-delete-reason"
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
              <input
                type="checkbox"
                checked={confirm.notify}
                onChange={(e) => setConfirm({ ...confirm, notify: e.target.checked })}
                data-testid="admin-delete-notify"
              />
              {t('adminJobs.notifyEmployer')}
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirm(null)} data-testid="admin-delete-cancel">{t('adminJobs.cancel')}</Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={submitting}
                data-testid="admin-delete-confirm"
              >
                {submitting ? t('adminJobs.removing') : t('adminJobs.removePermanently')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de resultado do Post Story */}
      {postResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="post-story-result-modal">
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Image className="h-5 w-5 text-indigo-600" />
                <h2 className="text-lg font-bold">Imagens geradas!</h2>
              </div>
              <button onClick={() => setPostResult(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Imagens criadas para: <strong>{postResult.jobTitle}</strong>
            </p>
            <div className="space-y-3">
              <a
                href={postResult.story_url}
                download={postResult.story_filename}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-colors text-indigo-700 font-medium text-sm"
                data-testid="download-story"
              >
                <Download className="h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">Story 9:16</div>
                  <div className="text-xs text-indigo-500 font-normal">1080 × 1920 px · Instagram / TikTok</div>
                </div>
              </a>
              <a
                href={postResult.feed_url}
                download={postResult.feed_filename}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl border border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors text-purple-700 font-medium text-sm"
                data-testid="download-feed"
              >
                <Download className="h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">Feed 4:5</div>
                  <div className="text-xs text-purple-500 font-normal">1080 × 1350 px · Instagram Feed</div>
                </div>
              </a>
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center">Clica nos links para descarregar as imagens</p>
          </Card>
        </div>
      )}
      {modAction && <ModerationDialog job={modAction.job} action={modAction.action} onClose={() => setModAction(null)} onDone={(j) => { setModAction(null); setItems((all) => all.map((x) => (x.id === j.id ? { ...x, ...j } : x))); }} />}
    </div>
  );
}
